// =============================================================================
// SIREPPAR - Consultas de partidos: detalle para la SEP (#390)
// =============================================================================
// Hilo de mensajes, referencia a la observación o intimación (solo lectura),
// asignación, respuesta ("Responder" o "Responder y cerrar") y notas internas.
// Responder, asignar y anotar requieren un rol de escritura; AUDITOR y
// MESA_ENTRADAS ven todo en modo lectura.
// =============================================================================

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import RichTextEditor, { MarkdownDisplay } from '../../components/common/RichTextEditor';
import AdjuntosInforme, { MAX_ADJUNTOS, MAX_MB_ADJUNTO } from '../../components/common/AdjuntosInforme';
import TextoColapsable from '../../components/common/TextoColapsable';
import EstadoConsultaBadge from '../../components/common/EstadoConsultaBadge';
import { ConfirmModal } from '../../components/common/Modal';
import { useAuth } from '../../contexts/AuthContext';
import { consultaSepService } from '../../services/api';
import { CLAVE_CONSULTAS_PENDIENTES } from '../../hooks/useConsultasPendientes';
import {
  EstadoConsulta,
  OrigenMensaje,
  TEXTO_MAX,
  formatearFecha,
  formatearFechaHora,
  formatearTamanio,
  mensajeDeError,
  puedeGestionarConsultas,
} from '../../utils/consultas';

interface UsuarioResumen {
  id: string;
  nombre: string;
  apellido: string;
}

interface Adjunto {
  id: string;
  nombreOriginal: string;
  mimeType: string;
  tamanioBytes: number;
}

interface Mensaje {
  id: string;
  texto: string;
  origen: OrigenMensaje;
  creadoEn: string;
  autor: { nombre: string; apellido: string } | null;
  adjuntos: Adjunto[];
}

interface NotaInterna {
  id: string;
  texto: string;
  creadoEn: string;
  autor: UsuarioResumen | null;
}

interface ConsultaSep {
  id: string;
  asunto: string;
  estado: EstadoConsulta;
  asignadaEn: string | null;
  cerradaEn: string | null;
  partido: { id: string; denominacion: string; numero: number | null };
  asignadaA: UsuarioResumen | null;
  cerradaPor: UsuarioResumen | null;
  expediente: { id: string; numero: string } | null;
  observacion: {
    id: string;
    tipo: string;
    texto: string;
    estado: string;
    fechaLimite: string | null;
    subsanada: boolean;
    creadoEn: string;
  } | null;
  mensajes: Mensaje[];
  notasInternas: NotaInterna[];
  partidoConIntimacionVigente: boolean;
  iniciadaPor: { nombre: string; apellido: string; origen: OrigenMensaje } | null;
}

const nombre = (u: { nombre: string; apellido: string } | null) => (u ? `${u.nombre} ${u.apellido}` : '—');

export default function GestionConsultaDetailPage() {
  const { id = '' } = useParams();
  const { user } = useAuth();
  const puedeEscribir = puedeGestionarConsultas(user?.rol);
  const queryClient = useQueryClient();

  const [respuesta, setRespuesta] = useState('');
  const [archivos, setArchivos] = useState<File[]>([]);
  const [confirmarCierre, setConfirmarCierre] = useState(false);
  const [confirmarCierreSinRespuesta, setConfirmarCierreSinRespuesta] = useState(false);
  const [nota, setNota] = useState('');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['sep-consulta', id],
    queryFn: () => consultaSepService.getById(id),
    enabled: !!id,
  });
  const consulta: ConsultaSep | undefined = data?.data;

  const { data: asignables = [] } = useQuery<UsuarioResumen[]>({
    queryKey: ['sep-consultas-asignables'],
    queryFn: async () => (await consultaSepService.asignables())?.data ?? [],
    enabled: puedeEscribir,
  });

  const refrescar = () => {
    queryClient.invalidateQueries({ queryKey: ['sep-consulta', id] });
    queryClient.invalidateQueries({ queryKey: ['sep-consultas'] });
    queryClient.invalidateQueries({ queryKey: CLAVE_CONSULTAS_PENDIENTES });
  };

  const cerrarSinResponder = useMutation({
    mutationFn: () => consultaSepService.cerrar(id),
    onSuccess: () => {
      toast.success('Consulta cerrada');
      setConfirmarCierreSinRespuesta(false);
      refrescar();
    },
    onError: (e) => {
      setConfirmarCierreSinRespuesta(false);
      toast.error(mensajeDeError(e, 'No se pudo cerrar la consulta'));
    },
  });

  const asignar = useMutation({
    mutationFn: (usuarioId: string | null) => consultaSepService.asignar(id, usuarioId),
    onSuccess: (_r, usuarioId) => {
      toast.success(usuarioId ? 'Consulta asignada' : 'Consulta desasignada');
      refrescar();
    },
    onError: (e) => toast.error(mensajeDeError(e, 'No se pudo cambiar la asignación')),
  });

  const responder = useMutation({
    mutationFn: (cerrar: boolean) => {
      const datos = new FormData();
      datos.append('texto', respuesta.trim());
      datos.append('cerrar', cerrar ? 'true' : 'false');
      archivos.forEach((archivo) => datos.append('archivos', archivo));
      return consultaSepService.responder(id, datos);
    },
    onSuccess: (_r, cerrar) => {
      toast.success(cerrar ? 'Respuesta enviada y consulta cerrada' : 'Respuesta enviada');
      setRespuesta('');
      setArchivos([]);
      setConfirmarCierre(false);
      refrescar();
    },
    onError: (e) => {
      setConfirmarCierre(false);
      toast.error(mensajeDeError(e, 'No se pudo enviar la respuesta'));
    },
  });

  const agregarNota = useMutation({
    mutationFn: () => consultaSepService.crearNota(id, nota.trim()),
    onSuccess: () => {
      toast.success('Nota interna agregada');
      setNota('');
      refrescar();
    },
    onError: (e) => toast.error(mensajeDeError(e, 'No se pudo agregar la nota')),
  });

  const abrirAdjunto = async (adjuntoId: string) => {
    try {
      const res = await consultaSepService.getAdjuntoUrl(id, adjuntoId);
      window.open(res.data.url, '_blank', 'noopener');
    } catch (e) {
      toast.error(mensajeDeError(e, 'No se pudo abrir el adjunto'));
    }
  };

  if (isLoading) return <p className="text-sm text-gray-500">Cargando...</p>;

  if (isError || !consulta) {
    return (
      <div className="max-w-4xl mx-auto bg-white rounded-lg border border-gray-200 p-6 text-center">
        <p className="text-gray-700">No se encontró la consulta.</p>
        <Link to="/gestion-consultas" className="text-sm text-blue-600 hover:underline mt-2 inline-block">Volver a la bandeja</Link>
      </div>
    );
  }

  const respuestaLimpia = respuesta.trim();
  const errorRespuesta = respuestaLimpia.length > TEXTO_MAX ? `La respuesta puede tener hasta ${TEXTO_MAX} caracteres.` : '';
  const puedeResponder = respuestaLimpia.length > 0 && !errorRespuesta && !responder.isPending;
  const notaLimpia = nota.trim();
  const cerrada = consulta.estado === 'CERRADA';

  return (
    <div className="max-w-4xl mx-auto">
      <Link to="/gestion-consultas" className="text-sm text-blue-600 hover:underline">← Volver a la bandeja</Link>

      {/* Encabezado */}
      <div className="mt-3 mb-6 bg-white rounded-lg border border-gray-200 p-4">
        <p className="text-sm text-gray-600">
          {consulta.partido.numero != null ? `${consulta.partido.numero} — ` : ''}{consulta.partido.denominacion}
          {consulta.partidoConIntimacionVigente && (
            <span className="ml-2 inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
              Intimación vigente
            </span>
          )}
        </p>
        <div className="flex flex-wrap items-center gap-3 mt-1">
          <h1 className="text-2xl font-bold text-gray-900">{consulta.asunto}</h1>
          <EstadoConsultaBadge estado={consulta.estado} />
        </div>
        {cerrada && (
          <p className="text-xs text-gray-500 mt-1">
            Cerrada el {formatearFechaHora(consulta.cerradaEn)} por {nombre(consulta.cerradaPor)}
          </p>
        )}
        {consulta.iniciadaPor && (
          <p className="text-xs text-gray-500 mt-1">
            Iniciada por {nombre(consulta.iniciadaPor)} ({consulta.iniciadaPor.origen === 'SEP' ? 'SEP' : 'Partido'})
          </p>
        )}
        <div className="mt-3 text-sm text-gray-700 flex flex-wrap items-center gap-2">
          <span className="font-medium">Asignada a:</span>
          {puedeEscribir ? (
            <select
              value={consulta.asignadaA?.id ?? ''}
              onChange={(e) => asignar.mutate(e.target.value || null)}
              disabled={asignar.isPending}
              className="border border-gray-300 rounded-md px-2 py-1 text-sm"
            >
              <option value="">Sin asignar</option>
              {asignables.map((u) => (
                <option key={u.id} value={u.id}>{u.nombre} {u.apellido}</option>
              ))}
              {consulta.asignadaA && !asignables.some((u) => u.id === consulta.asignadaA?.id) && (
                <option value={consulta.asignadaA.id}>{nombre(consulta.asignadaA)}</option>
              )}
            </select>
          ) : (
            <span>{consulta.asignadaA ? nombre(consulta.asignadaA) : 'Sin asignar'}</span>
          )}
          {puedeEscribir && !cerrada && (
            <button
              type="button"
              onClick={() => setConfirmarCierreSinRespuesta(true)}
              disabled={cerrarSinResponder.isPending}
              className="ml-auto px-3 py-1.5 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Cerrar sin responder
            </button>
          )}
        </div>
      </div>

      {/* Referencia a la observación o intimación */}
      {consulta.observacion && consulta.expediente && (
        <div className="mb-6 rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
            <span className="font-semibold text-gray-800">
              {consulta.observacion.tipo === 'INTIMACION' ? 'Intimación' : 'Observación'} del expediente {consulta.expediente.numero}
            </span>
            <Link to={`/expedientes/${consulta.expediente.id}?tab=obs`} className="text-blue-600 hover:underline">Ver en el expediente →</Link>
          </div>
          <p className="text-xs text-gray-600 mb-2">
            Estado: {consulta.observacion.estado}
            {consulta.observacion.subsanada ? ' · subsanada' : ''}
            {' · '}Fecha límite: {formatearFecha(consulta.observacion.fechaLimite)}
          </p>
          <TextoColapsable texto={consulta.observacion.texto} tope={300} className="text-gray-700" />
        </div>
      )}

      {/* Hilo */}
      <div className="space-y-4">
        {consulta.mensajes.map((mensaje) => {
          const deLaSep = mensaje.origen === 'SEP';
          return (
            <div
              key={mensaje.id}
              className={`rounded-lg border p-4 ${deLaSep ? 'border-green-200 bg-green-50 ml-8' : 'border-gray-200 bg-white mr-8'}`}
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
                <span className="text-sm font-semibold text-gray-800">
                  {deLaSep ? 'Secretaría' : 'Partido'}
                  {mensaje.autor && <span className="font-normal text-gray-500"> · {nombre(mensaje.autor)}</span>}
                </span>
                <span className="text-xs text-gray-500">{formatearFechaHora(mensaje.creadoEn)}</span>
              </div>
              <div className="text-sm text-gray-800">
                <MarkdownDisplay content={mensaje.texto} />
              </div>
              {mensaje.adjuntos.length > 0 && (
                <ul className="mt-3 space-y-1">
                  {mensaje.adjuntos.map((adjunto) => (
                    <li key={adjunto.id}>
                      <button type="button" onClick={() => abrirAdjunto(adjunto.id)} className="text-sm text-blue-600 hover:underline">
                        {adjunto.nombreOriginal}
                      </button>
                      <span className="text-xs text-gray-500"> ({formatearTamanio(adjunto.tamanioBytes)})</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>

      {/* Respuesta */}
      {puedeEscribir && !cerrada && (
        <form
          className="mt-6 bg-white rounded-lg border border-gray-200 p-4 space-y-4"
          onSubmit={(e) => { e.preventDefault(); if (puedeResponder) responder.mutate(false); }}
        >
          <span className="block text-sm font-medium text-gray-700">Responder al partido</span>
          <RichTextEditor value={respuesta} onChange={setRespuesta} placeholder="Escriba la respuesta" minHeight={140} />
          {errorRespuesta && <p className="text-xs text-red-600">{errorRespuesta}</p>}
          <AdjuntosInforme
            archivos={archivos}
            onChange={setArchivos}
            ayuda={`Hasta ${MAX_ADJUNTOS} archivos PDF o imagen, de ${MAX_MB_ADJUNTO} MB cada uno.`}
          />
          <div className="flex flex-wrap justify-end gap-3">
            <button
              type="button"
              disabled={!puedeResponder}
              onClick={() => setConfirmarCierre(true)}
              className="px-4 py-2 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Responder y cerrar
            </button>
            <button
              type="submit"
              disabled={!puedeResponder}
              className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium disabled:opacity-50"
            >
              {responder.isPending ? 'Enviando...' : 'Responder'}
            </button>
          </div>
        </form>
      )}

      <ConfirmModal
        isOpen={confirmarCierre}
        onClose={() => setConfirmarCierre(false)}
        onConfirm={() => responder.mutate(true)}
        title="Responder y cerrar la consulta"
        message="Se envía la respuesta y la consulta queda cerrada: el partido ya no va a poder escribir en ella."
        confirmText="Responder y cerrar"
        variant="warning"
        loading={responder.isPending}
      />

      <ConfirmModal
        isOpen={confirmarCierreSinRespuesta}
        onClose={() => setConfirmarCierreSinRespuesta(false)}
        onConfirm={() => cerrarSinResponder.mutate()}
        title="Cerrar sin responder"
        message="La consulta se cierra sin agregar una respuesta y no se le avisa al partido. Después de cerrarla, el partido ya no va a poder escribir en ella."
        confirmText="Cerrar consulta"
        variant="warning"
        loading={cerrarSinResponder.isPending}
      />

      {/* Notas internas */}
      <section className="mt-8 rounded-lg border border-amber-300 bg-amber-50 p-4">
        <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
          <h2 className="text-base font-semibold text-amber-900">Notas internas</h2>
          <span className="text-xs font-medium text-amber-800">No visible para el partido</span>
        </div>
        {consulta.notasInternas.length === 0 ? (
          <p className="text-sm text-amber-900/70">Todavía no hay notas internas.</p>
        ) : (
          <ul className="space-y-3">
            {consulta.notasInternas.map((n) => (
              <li key={n.id} className="text-sm">
                <p className="text-xs text-amber-800">{nombre(n.autor)} · {formatearFechaHora(n.creadoEn)}</p>
                <p className="text-gray-800 whitespace-pre-wrap">{n.texto}</p>
              </li>
            ))}
          </ul>
        )}
        {puedeEscribir && (
          <form
            className="mt-4 flex flex-col gap-2"
            onSubmit={(e) => { e.preventDefault(); if (notaLimpia && !agregarNota.isPending) agregarNota.mutate(); }}
          >
            <textarea
              value={nota}
              onChange={(e) => setNota(e.target.value)}
              rows={2}
              maxLength={TEXTO_MAX}
              placeholder="Agregar una nota interna..."
              className="w-full border border-amber-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={!notaLimpia || agregarNota.isPending}
                className="px-4 py-1.5 text-sm bg-amber-600 hover:bg-amber-700 text-white rounded-md disabled:opacity-50"
              >
                {agregarNota.isPending ? 'Guardando...' : 'Agregar nota'}
              </button>
            </div>
          </form>
        )}
      </section>
    </div>
  );
}
